import { MapAreaService, MapAreaRenderer, MapAreaAnimator } from '@/modules/map-editor';
import type {
  MapAreaState,
  MapAreaConfig,
  MapImageConfig,
  DrawAreaOverride,
  TransferAnimationCallbacks,
  TransferTarget
} from '@/modules/map-editor';
import { ref, watch, type Ref } from 'vue';

export interface UseMapAreaReturn {
  mapArea: MapAreaState;
  mapAreaService: MapAreaService;
  mapAreaRenderer: MapAreaRenderer;
  mapAreaAnimator: MapAreaAnimator;
  windowDimensions: Ref<{ width: number; height: number }>;
  saveWindowDimensions: (pixelRatio: number) => void;
  updateMapAreaCoords: (pixelRatio: number) => void;
  mapBgWatcher: (mapCanvasHTML: HTMLElement, mapBgColor: any) => void;
  drawMapArea: (
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    ignoreMapCoords?: boolean,
    area?: DrawAreaOverride
  ) => void;
  drawMapImage: (
    ctx: CanvasRenderingContext2D,
    zoomLevel: number,
    mapData: MapImageConfig
  ) => void;
  transferMapArea: (
    target?: TransferTarget | null,
    callbacks?: TransferAnimationCallbacks
  ) => void;
  cancelTransfer: () => void;
  resetCoordinates: () => void;
}

/**
 * Composable for map area management.
 * Orchestrates all map area services.
 * Returns singleton instance of MapAreaService.
 *
 * @param config - Optional initial configuration for the map area
 * @returns Map area state, services, and helper functions
 */
export function useMapArea(config?: MapAreaConfig): UseMapAreaReturn {
  const mapAreaService = MapAreaService.getInstance(config);
  const mapAreaRenderer = new MapAreaRenderer();
  const mapAreaAnimator = new MapAreaAnimator(mapAreaService);

  /** Browser window dimensions scaled by pixel ratio, used for resize-based repositioning. */
  const windowDimensions = ref({ width: 0, height: 0 });

  /**
   * Snapshot current window dimensions (scaled by pixel ratio).
   * Must be called once at init and after every resize so that
   * `updateMapAreaCoords` can compute the delta.
   *
   * @param pixelRatio - Device pixel ratio owned by useCanvasManager
   */
  function saveWindowDimensions(pixelRatio: number): void {
    windowDimensions.value = {
      width: window.innerWidth * pixelRatio,
      height: window.innerHeight * pixelRatio
    };
  }

  /**
   * Recalculate map area position after a window resize so the map
   * stays visually centred.
   *
   * @param pixelRatio - Device pixel ratio owned by useCanvasManager
   */
  function updateMapAreaCoords(pixelRatio: number): void {
    const currentWidth = window.innerWidth * pixelRatio;
    const currentHeight = window.innerHeight * pixelRatio;

    const mapAreaState = mapAreaService.getState();

    // Shift map position by half the size delta to keep it centred
    mapAreaState.x += (currentWidth - windowDimensions.value.width) / 2;
    mapAreaState.y += (currentHeight - windowDimensions.value.height) / 2;

    saveWindowDimensions(pixelRatio);
    mapAreaService.updateElement();
  }

  /**
   * Watcher for map background color changes
   */
  function mapBgWatcher(
    mapCanvasHTML: HTMLElement,
    mapBgColor: any
  ): void {
    watch(mapBgColor, (value, oldValue) => {
      if (!oldValue) {
        mapCanvasHTML.style.cssText = value;
        return;
      }

      // Because of glitch related to changing a gradient type in Chrome
      if (
        !oldValue.includes('linear-gradient') ||
        !oldValue.includes('radial-gradient')
      ) {
        mapCanvasHTML.style.cssText = '';
        setTimeout(() => {
          mapCanvasHTML.style.cssText = value;
        }, 10);
      } else {
        mapCanvasHTML.style.cssText = value;
      }
    }, { immediate: true });
  }

  return {
    mapArea: mapAreaService.getState(),
    mapAreaService,
    mapAreaRenderer,
    mapAreaAnimator,
    windowDimensions,
    saveWindowDimensions,
    updateMapAreaCoords,
    mapBgWatcher,
    drawMapArea: (
      ctx: CanvasRenderingContext2D,
      zoomLevel: number,
      ignoreMapCoords = false,
      area = { width: null, height: null, shiftX: 0, shiftY: 0 }
    ) => {
      mapAreaRenderer.drawMapArea(
        ctx,
        zoomLevel,
        mapAreaService.getState(),
        ignoreMapCoords,
        area
      );
    },
    drawMapImage: (
      ctx: CanvasRenderingContext2D,
      zoomLevel: number,
      mapData: MapImageConfig
    ) => {
      mapAreaRenderer.drawMapImage(
        ctx,
        zoomLevel,
        mapAreaService.getState(),
        mapData
      );
    },
    transferMapArea: (
      target?: TransferTarget | null,
      callbacks?: TransferAnimationCallbacks
    ) => {
      mapAreaAnimator.transferMapArea(target, callbacks);
    },
    cancelTransfer: () => {
      mapAreaAnimator.cancel();
    },
    resetCoordinates: () => {
      mapAreaService.resetCoordinates();
    }
  };
}

/**
 * Get MapAreaService instance (for use in Vuex stores)
 */
export function getMapAreaService(): MapAreaService {
  return MapAreaService.getInstance();
}